import { supabase } from '@/integrations/supabase/client';

// Helper function to check if a table exists in the database
export const checkTableExists = async (tableName: string): Promise<boolean> => {
  try {
    // Try to select a single row with a limit to minimize data transfer
    const { error } = await supabase
      .from(tableName)
      .select('*')
      .limit(1);
    
    // If there's no error, the table exists
    if (!error) {
      return true;
    }
    
    // Check if the error is specifically a 404 (table not found)
    if (error.code === '42P01' || error.message.includes('does not exist')) {
      console.log(`Table ${tableName} does not exist`);
      return false;
    }
    
    // For other errors, log but assume the table might exist
    console.warn(`Error checking if table ${tableName} exists:`, error);
    return false;
  } catch (error) {
    console.error(`Exception checking if table ${tableName} exists:`, error);
    return false;
  }
};

// Safe API request that handles common errors
export const safeApiRequest = async <T>(
  requestFn: () => Promise<{ data: T | null; error: any }>,
  fallbackValue: T | null = null,
  errorHandler?: (error: any) => void
): Promise<{ data: T | null; error: any | null }> => {
  try {
    const { data, error } = await requestFn();
    
    if (error) {
      console.log('API request error:', error);
      if (errorHandler) {
        errorHandler(error);
      }
      return { data: fallbackValue, error };
    }
    
    return { data, error: null };
  } catch (exception) {
    console.error('Exception during API request:', exception);
    if (errorHandler) {
      errorHandler(exception);
    }
    return { data: fallbackValue, error: exception };
  }
};

// Helper to safely update a profile with fallbacks for different schema versions
export const safeProfileUpdate = async (
  userId: string,
  updates: Record<string, any>
): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    // First attempt: try with all fields
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);
    
    if (!error) {
      return true;
    }
    
    console.log('Profile update error, trying fallback approaches:', error);
    
    // If there's an error about updated_at, remove it and try again
    if (error.message.includes('updated_at')) {
      const { updated_at, ...updatesWithoutTimestamp } = updates;
      
      const { error: retryError } = await supabase
        .from('profiles')
        .update(updatesWithoutTimestamp)
        .eq('id', userId);
      
      if (!retryError) {
        return true;
      }
      
      console.log('Profile update still failed after removing updated_at:', retryError);
    }
    
    // Try with just subscription_status as a last resort
    if ('subscription_status' in updates) {
      const { error: statusError } = await supabase
        .from('profiles')
        .update({
          subscription_status: updates.subscription_status
        })
        .eq('id', userId);
      
      if (!statusError) {
        console.log('Successfully updated profile with just subscription_status');
        return true;
      }
      
      console.log('Failed to update even with minimal fields:', statusError);
    }
    
    return false;
  } catch (error) {
    console.error('Exception during profile update:', error);
    return false;
  }
};
